import {
  forwardRef,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect
} from 'react';
import type { MutableRefObject, PropsWithoutRef } from 'react';

import type { ForwardRefForwardPropsComponent } from '../../types';
import {
  useAfterInitialEffect,
  useConsolidatedRef,
  useI18n,
  useItemIntersection,
  useLiveLog
} from '../../hooks';
import Progress from '../Progress';
import EmptyState from '../EmptyState';
import Popover from '../Popover';

import type { MenuListProps, ParentMenuItemProps } from './Menu.types';
import MenuContext from './Menu.context';
import MenuItem from './MenuItem';
import { StyledLoadingItem, StyledMenuList, StyledFlyoutMenuListContainer } from './Menu.styles';
import { resizeRootEl } from './NavItemsList';
import helpers from './helpers';
import MenuGroup from './MenuGroup';
import { isParentMenuItem } from './Menu.utils';

const FlyoutMenuList: ForwardRefForwardPropsComponent<MenuListProps> = forwardRef(
  function FlyoutMenuListFunction(
    { items, parent, menuRole }: PropsWithoutRef<MenuListProps>,
    ref: MenuListProps['ref']
  ) {
    const t = useI18n();
    const selfRef = useConsolidatedRef(ref);
    const ulRef = useRef<HTMLUListElement>(null);
    const menuListWrapperRef: MutableRefObject<HTMLElement | null> = useRef(null);
    const returnFocusRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
    const [expandedItem, setExpandedItem] = useState<ParentMenuItemProps>();
    const {
      componentId,
      loadMore,
      loading,
      scrollAt,
      emptyText,
      onItemExpand,
      pushFlyoutId,
      flyOutActiveIdStack,
      updateActiveDescendants,
      updateParentDescendantStack
    } = useContext(MenuContext);
    const { announcePolite } = useLiveLog();

    useItemIntersection(
      ulRef,
      items.length - 1,
      () => {
        loadMore?.(parent?.item?.id);
      },
      ':scope > li'
    );

    useAfterInitialEffect(() => {
      if (expandedItem) {
        const foundItem = items.find(item => item.id === expandedItem.id);
        if (foundItem && isParentMenuItem(foundItem)) {
          setExpandedItem(foundItem);
        } else {
          setExpandedItem(undefined);
        }
      }
    }, [expandedItem, items]);

    useEffect(() => {
      const hasExpanded = items.some(item => {
        return flyOutActiveIdStack.includes(item.id);
      });

      if (!hasExpanded) {
        returnFocusRef.current = null;
        setExpandedItem(undefined);
      }
    }, [flyOutActiveIdStack]);

    useEffect(() => {
      if (items.length === 0 && !loading) {
        announcePolite({ message: emptyText ?? t('no_items') });
      }
    }, [items.length, loading]);

    const listContent = useMemo(() => {
      if (items.length) {
        return items.map(item => {
          return helpers.isItem(item) ? (
            <MenuItem
              {...item}
              key={item.id}
              aria-haspopup={!!item.items}
              aria-expanded={item.items ? item.id === expandedItem?.id : undefined}
              role={menuRole === 'listbox' ? 'option' : 'menuitem'}
              onExpand={(id, e) => {
                if (item.items) {
                  returnFocusRef.current = e.currentTarget as HTMLButtonElement;
                  setExpandedItem(isParentMenuItem(item) ? item : undefined);
                  item?.onExpand?.(id, e);
                  onItemExpand?.(id, e);
                  pushFlyoutId(item.id || componentId);
                  updateActiveDescendants();
                  if (updateParentDescendantStack) {
                    updateParentDescendantStack(returnFocusRef.current.closest('li'));
                  }
                } else {
                  returnFocusRef.current = null;
                  setExpandedItem(undefined);
                }
              }}
            />
          ) : (
            <MenuGroup
              {...item}
              key={item.id}
              itemRole={menuRole === 'listbox' ? 'option' : 'menuitem'}
            />
          );
        });
      }

      if (!loading) {
        return <EmptyState message={emptyText} forwardedAs='li' />;
      }

      return null;
    }, [items, loading, emptyText, expandedItem, flyOutActiveIdStack]);

    const list = (
      <StyledMenuList ref={ulRef} role={menuRole}>
        {listContent}
        {loading && !expandedItem && (
          <StyledLoadingItem>
            <Progress placement='local' />
          </StyledLoadingItem>
        )}
      </StyledMenuList>
    );

    useLayoutEffect(() => {
      setExpandedItem(undefined);
      if (selfRef.current) {
        menuListWrapperRef.current = selfRef.current.parentElement;
        resizeRootEl(selfRef.current, selfRef.current, items.length, scrollAt);
      }
    }, [items]);

    const handleMenuListScroll = useCallback(() => {
      if (expandedItem) {
        setExpandedItem(undefined);
      }
    }, [expandedItem]);

    return (
      <>
        <StyledFlyoutMenuListContainer
          ref={selfRef}
          onScroll={handleMenuListScroll}
          data-flyout-menu-parent-id={componentId}
          data-flyout-menu-id={parent?.item.id || componentId}
        >
          {list}
        </StyledFlyoutMenuListContainer>

        {expandedItem && selfRef.current && (
          <Popover
            target={returnFocusRef.current}
            show
            strategy='fixed'
            placement='right-start'
            portal={false}
          >
            <FlyoutMenuList
              items={expandedItem.items}
              parent={{
                el: selfRef.current,
                item: expandedItem,
                siblingItems: items,
                setExpandedItem,
                returnFocusRef
              }}
              menuRole={menuRole}
            />
          </Popover>
        )}
      </>
    );
  }
);

export default FlyoutMenuList;
